import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';

const QuickRepliesView: React.FC = () => {
  const { user } = useAuth();
  const [quickReplies, setQuickReplies] = useState<any[]>([]);
  const [showQuickReplyForm, setShowQuickReplyForm] = useState(false);
  const [editingQuickReply, setEditingQuickReply] = useState<any>(null);
  const [quickReplyForm, setQuickReplyForm] = useState({
    shortcut: '',
    content: '',
    isGlobal: false
  });
  
  // Auto Reply states
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplyMessage, setAutoReplyMessage] = useState('');
  const [autoReplyStartTime, setAutoReplyStartTime] = useState('18:00');
  const [autoReplyEndTime, setAutoReplyEndTime] = useState('09:00');
  const [loadingAutoReply, setLoadingAutoReply] = useState(true);

  useEffect(() => {
    loadQuickReplies();
    loadAutoReplySettings();
  }, []);

  const loadQuickReplies = async () => {
    try {
      // axios already has /api as baseURL and auto-adds auth header
      const response = await axios.get('/quick-replies', {
        params: {
          userId: user?.id
        }
      });
      setQuickReplies(response.data);
    } catch (error) {
      console.error('Error loading quick replies:', error);
    }
  };

  const handleQuickReplySubmit = async () => {
    try {
      // axios already has /api as baseURL and auto-adds auth header
      const payload = {
        ...quickReplyForm,
        userId: user?.id
      };

      if (editingQuickReply) {
        await axios.put(`/quick-replies/${editingQuickReply.id}`, payload);
      } else {
        await axios.post('/quick-replies', payload);
      }

      loadQuickReplies();
      setShowQuickReplyForm(false);
      setEditingQuickReply(null);
      setQuickReplyForm({ shortcut: '', content: '', isGlobal: false });
    } catch (error: any) {
      console.error('Error saving quick reply:', error);
      alert(error.response?.data?.error || 'Failed to save quick reply');
    }
  };

  const handleDeleteQuickReply = async (id: string) => {
    if (!window.confirm('Delete this quick reply?')) return;
    
    try {
      // axios already has /api as baseURL and auto-adds auth header
      await axios.delete(`/quick-replies/${id}`, {
        params: {
          userId: user?.id
        }
      });
      
      loadQuickReplies();
    } catch (error) {
      console.error('Error deleting quick reply:', error);
      alert('Failed to delete quick reply');
    }
  };

  const handleEditQuickReply = (quickReply: any) => {
    setEditingQuickReply(quickReply);
    setQuickReplyForm({
      shortcut: quickReply.shortcut,
      content: quickReply.content,
      isGlobal: quickReply.isGlobal
    });
    setShowQuickReplyForm(true);
  };

  const loadAutoReplySettings = async () => {
    try {
      // axios already has /api as baseURL and auto-adds auth header
      const response = await axios.get('/settings/auto-reply');
      
      if (response.data.success && response.data.data) {
        const settings = response.data.data;
        setAutoReplyEnabled(settings.enabled || false);
        setAutoReplyMessage(settings.message || '');
        setAutoReplyStartTime(settings.startTime || '18:00');
        setAutoReplyEndTime(settings.endTime || '09:00');
      }
    } catch (error) {
      console.error('Error loading auto reply settings:', error);
    } finally {
      setLoadingAutoReply(false);
    }
  };

  const saveAutoReplySettings = async () => {
    try {
      // axios already has /api as baseURL and auto-adds auth header
      await axios.put('/settings/auto-reply', {
        enabled: autoReplyEnabled,
        message: autoReplyMessage,
        startTime: autoReplyStartTime,
        endTime: autoReplyEndTime
      });
      
      alert('Auto reply settings saved successfully!');
    } catch (error) {
      console.error('Error saving auto reply settings:', error);
      alert('Failed to save auto reply settings');
    }
  };

  return (
    <div className="settings-view">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        padding: '0 24px'
      }}>
        <div>
          <h2 className="settings-title" style={{ marginBottom: '8px' }}>Quick Replies</h2>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
            Create and manage pre-made responses for faster messaging
          </p>
        </div>
        <button
          onClick={() => {
            setEditingQuickReply(null);
            setQuickReplyForm({ shortcut: '', content: '', isGlobal: false });
            setShowQuickReplyForm(true);
          }}
          className="add-contact-button"
        >
          + Add Quick Reply
        </button>
      </div>

      <div className="settings-section">
        {showQuickReplyForm && (
          <div style={{
            backgroundColor: '#f9fafb',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
              {editingQuickReply ? 'Edit Quick Reply' : 'New Quick Reply'}
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '13px', 
                fontWeight: '500', 
                marginBottom: '6px', 
                color: '#374151' 
              }}>
                Shortcut <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={quickReplyForm.shortcut}
                onChange={(e) => setQuickReplyForm({...quickReplyForm, shortcut: e.target.value})}
                placeholder="/hello, /thanks, /info"
                className="form-input"
                style={{ width: '100%' }}
              />
              <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Type this shortcut in the message box to use this quick reply
              </small>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '13px', 
                fontWeight: '500', 
                marginBottom: '6px', 
                color: '#374151' 
              }}>
                Message Content <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                value={quickReplyForm.content}
                onChange={(e) => setQuickReplyForm({...quickReplyForm, content: e.target.value})}
                placeholder="Hello! How can I help you today?"
                className="form-input"
                style={{ width: '100%', minHeight: '100px', resize: 'vertical' }}
              />
            </div>

            {user?.role === 'ADMIN' && (
              <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="isGlobal"
                  checked={quickReplyForm.isGlobal}
                  onChange={(e) => setQuickReplyForm({...quickReplyForm, isGlobal: e.target.checked})}
                  style={{ cursor: 'pointer' }}
                />
                <label htmlFor="isGlobal" style={{ fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                  Make available to all agents
                </label>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowQuickReplyForm(false);
                  setEditingQuickReply(null);
                  setQuickReplyForm({ shortcut: '', content: '', isGlobal: false });
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleQuickReplySubmit}
                disabled={!quickReplyForm.shortcut || !quickReplyForm.content}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  backgroundColor: quickReplyForm.shortcut && quickReplyForm.content ? '#3b82f6' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: quickReplyForm.shortcut && quickReplyForm.content ? 'pointer' : 'not-allowed'
                }}
              >
                {editingQuickReply ? 'Update Quick Reply' : 'Save Quick Reply'}
              </button>
            </div>
          </div>
        )}

        {quickReplies.length === 0 && !showQuickReplyForm ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
            <h3 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#111827' 
            }}>
              No quick replies yet
            </h3>
            <p style={{ 
              margin: '0', 
              color: '#6b7280', 
              fontSize: '14px',
              maxWidth: '400px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              Create your first quick reply to speed up your responses
            </p>
          </div>
        ) : quickReplies.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
            {quickReplies.map((qr) => (
              <div
                key={qr.id}
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  transition: 'box-shadow 0.2s',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <code style={{
                      backgroundColor: '#eff6ff',
                      color: '#1e40af',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}>
                      {qr.shortcut}
                    </code>
                    {qr.isGlobal && (
                      <span style={{
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '500'
                      }}>
                        Global
                      </span>
                    )}
                  </div>
                  {qr.userId !== user?.id && (
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Created by {qr.user?.name}
                    </div>
                  )}
                </div>

                <div style={{ 
                  fontSize: '14px', 
                  color: '#374151', 
                  whiteSpace: 'pre-wrap',
                  marginBottom: '16px',
                  lineHeight: '1.5',
                  maxHeight: '100px',
                  overflowY: 'auto'
                }}>
                  {qr.content}
                </div>

                {(qr.userId === user?.id || user?.role === 'ADMIN') && (
                  <div style={{ display: 'flex', gap: '8px', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
                    <button
                      onClick={() => handleEditQuickReply(qr)}
                      style={{
                        flex: 1,
                        padding: '6px 12px',
                        fontSize: '13px',
                        backgroundColor: 'white',
                        color: '#3b82f6',
                        border: '1px solid #3b82f6',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteQuickReply(qr.id)}
                      style={{
                        flex: 1,
                        padding: '6px 12px',
                        fontSize: '13px',
                        backgroundColor: 'white',
                        color: '#ef4444',
                        border: '1px solid #ef4444',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Auto Reply Section */}
      <div className="settings-section" style={{ marginTop: '32px' }}>
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
            Auto Reply
          </h3>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
            Automatically send a message to new contacts during specific hours
          </p>
        </div>

        <div style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}>
          {/* Enable/Disable Toggle */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  Enable Auto Reply
                </h4>
                <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                  Send automatic responses during off-hours
                </p>
              </div>
              <div 
                onClick={() => setAutoReplyEnabled(!autoReplyEnabled)}
                style={{
                  width: '48px',
                  height: '24px',
                  backgroundColor: autoReplyEnabled ? '#3b82f6' : '#d1d5db',
                  borderRadius: '12px',
                  position: 'relative',
                  transition: 'background-color 0.2s',
                  cursor: 'pointer'
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: autoReplyEnabled ? '26px' : '2px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
                }} />
              </div>
            </div>
          </div>

          {/* Time Settings */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '16px', 
            marginBottom: '24px',
            opacity: autoReplyEnabled ? 1 : 0.5,
            pointerEvents: autoReplyEnabled ? 'auto' : 'none'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                marginBottom: '8px', 
                color: '#374151' 
              }}>
                Start Time (After Hours Begin)
              </label>
              <input
                type="time"
                value={autoReplyStartTime}
                onChange={(e) => setAutoReplyStartTime(e.target.value)}
                className="form-input"
                style={{ width: '100%' }}
                disabled={!autoReplyEnabled}
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                marginBottom: '8px', 
                color: '#374151' 
              }}>
                End Time (Business Hours Begin)
              </label>
              <input
                type="time"
                value={autoReplyEndTime}
                onChange={(e) => setAutoReplyEndTime(e.target.value)}
                className="form-input"
                style={{ width: '100%' }}
                disabled={!autoReplyEnabled}
              />
            </div>
          </div>

          {/* Auto Reply Message */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: '500', 
              marginBottom: '8px', 
              color: '#374151' 
            }}>
              Auto Reply Message
            </label>
            <textarea
              value={autoReplyMessage}
              onChange={(e) => setAutoReplyMessage(e.target.value)}
              placeholder="Thank you for your message. Our team is currently unavailable. We'll get back to you during business hours (9 AM - 6 PM)."
              className="form-input"
              style={{ 
                width: '100%', 
                minHeight: '100px', 
                resize: 'vertical',
                opacity: autoReplyEnabled ? 1 : 0.5
              }}
              disabled={!autoReplyEnabled}
            />
            <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              This message will be sent automatically to new conversations during the specified hours
            </small>
          </div>

          {/* Save Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={saveAutoReplySettings}
              style={{
                padding: '10px 20px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Save Auto Reply Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickRepliesView;

